import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customBaseUrl, httpGet, httpPost } from '../store/http';
import { NotificationManager } from 'react-notifications';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  alpha,
  IconButton,
} from '@mui/material';
import {
  FiArrowLeft,
  FiCalendar,
  FiCreditCard,
  FiDollarSign,
  FiInfo,
  FiActivity,
  FiClock,
} from 'react-icons/fi';
import LoanDashboardLayout from '../components/Layout/LoanDashboardLayout';
import moment from 'moment';
import { hideLoader, showLoader } from '../utils/loader';
import AdminLayout from "../components/Layout";

const AdminSummaryPage = ({ from }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [loanSummary, setLoanSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLoanSummary = useCallback(async () => {
    showLoader();
    setLoading(true);
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      if (!token) {
        window.location.href = '/loan-admin-login';
        return;
      }
      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/loans/admin/summary/${id}`,
        null,
        false,
        false,
        token
      );
      if (res.status) {
        setLoanSummary(res.data);
      } else {
        NotificationManager.error(res.message || 'Failed to fetch loan summary');
      }
    } catch (error) {
      console.error('Error fetching loan summary:', error);
      NotificationManager.error('Error fetching loan summary');
    } finally {
      hideLoader();
      setLoading(false);
    }
  }, [id, from]);

  useEffect(() => {
    fetchLoanSummary();
  }, [fetchLoanSummary]);

  const handleManualRepay = async (installment) => {
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      if (!token) {
        window.location.href = '/loan-admin-login';
        return;
      }

      const { amount, date: installment_date } = installment;
      const payload = { amount, installment_date };

      const res = await httpPost(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/manual-repay`,
        payload,
        false,
        token
      );

      if (res.status) {
        NotificationManager.success('Repayment successful');
        fetchLoanSummary();
      } else {
        NotificationManager.error(res.message || 'Repayment failed');
      }
    } catch (error) {
      console.error('Manual repayment error:', error);
      NotificationManager.error('Manual repayment failed');
    } finally {
      hideLoader();
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'disbursed':
      case 'paid':
        return 'success';
      case 'unpaid':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#ff6700' }} />
        </Box>
      </Layout>
    );
  }

  if (!loanSummary) {
    return (
      <Layout>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Loan summary not found.
          </Typography>
          <Button
            startIcon={<FiArrowLeft />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Layout>
    );
  }

  const stats = [
    {
      label: 'Loan Amount',
      value: `₦${Number(loanSummary.loan_amount).toLocaleString()}`,
      icon: <FiDollarSign size={20} />,
      color: theme.palette.primary.main,
    },
    {
      label: 'Total Paid',
      value: `₦${Number(loanSummary.total_paid).toLocaleString()}`,
      icon: <FiCreditCard size={20} />,
      color: theme.palette.success.main,
    },
    {
      label: 'Outstanding',
      value: `₦${Number(loanSummary.total_outstanding).toLocaleString()}`,
      icon: <FiActivity size={20} />,
      color: theme.palette.error.main,
    },
    {
      label: 'Next Payment',
      value: loanSummary.next_payment_date ? moment(loanSummary.next_payment_date).format('MMM DD, YYYY HH:mm') : 'N/A',
      icon: <FiCalendar size={20} />,
      color: theme.palette.info.main,
    },
  ];

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: alpha(theme.palette.divider, 0.05) }}>
            <FiArrowLeft size={20} />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>
              Loan Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {loanSummary.loan_id} • Account: {loanSummary.loan_account_number || 'N/A'}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Chip
              label={loanSummary.status}
              color={getStatusColor(loanSummary.status)}
              sx={{ fontWeight: 700, borderRadius: '8px', px: 1 }}
            />
          </Box>
        </Box>

        {/* Stats Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.shadows[2] }
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(stat.color, 0.1),
                    color: stat.color,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                    {stat.value}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Main Details */}
          <Grid item xs={12} lg={4}>
            <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <FiInfo size={20} color={theme.palette.primary.main} />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Customer Information</Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {[
                    { label: 'Full Name', value: `${loanSummary.customer?.first_name} ${loanSummary.customer?.last_name}` },
                    { label: 'Email Address', value: loanSummary.customer?.email },
                    { label: 'Phone Number', value: loanSummary.customer?.phone },
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{item.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{item.value || 'N/A'}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${theme.palette.divider}`, height: 'calc(100% - 240px)' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <FiInfo size={20} color={theme.palette.primary.main} />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Loan Information</Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {[
                    { label: 'Interest Rate', value: `${loanSummary.interest_rate}%` },
                    { label: 'Term Duration', value: `${loanSummary.term_months} Months` },
                    { label: 'Total Repayment', value: `₦${Number(loanSummary.total_repayment_amount).toLocaleString()}` },
                    { label: 'Start Date', value: loanSummary.loan_start_date ? moment(loanSummary.loan_start_date).format('YYYY-MM-DD HH:mm:ss') : 'N/A' },
                    { label: 'End Date', value: loanSummary.loan_end_date ? moment(loanSummary.loan_end_date).format('YYYY-MM-DD HH:mm:ss') : 'N/A' },
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{item.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>{item.value}</Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 4 }} />

                <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), p: 2, borderRadius: 2, border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}` }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiClock size={14} /> ACCOUNT NUMBER
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: theme.palette.primary.main, mt: 0.5 }}>
                    {loanSummary.loan_account_number || 'N/A'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Installment Plan */}
          <Grid item xs={12} lg={8}>
            <Card elevation={0} sx={{ borderRadius: 4, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCalendar size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Installment Schedule</Typography>
                  </Box>
                </Box>

                <TableContainer>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ bgcolor: alpha(theme.palette.divider, 0.03) }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>DUE DATE</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>PAID DATE</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>AMOUNT</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>STATUS</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>PAYMENT METHOD</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>REMARKS</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', py: 2 }}>ACTION</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loanSummary.installment_dates?.map((row, idx) => (
                        <TableRow key={idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {moment(row.date).format('MMM DD, YYYY HH:mm:ss')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {row.payment_date ? moment(row.payment_date).format('MMM DD, YYYY HH:mm:ss') : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              ₦{Number(row.amount).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={row.payment_status}
                              size="small"
                              color={getStatusColor(row.payment_status)}
                              sx={{ fontWeight: 700, borderRadius: '6px', fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {row.payment_method || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem' }}>
                              {row.notes || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            {row.payment_status?.toLowerCase() !== 'paid' && (
                              <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                onClick={() => handleManualRepay(row)}
                                sx={{
                                  borderRadius: '8px',
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  px: 2,
                                  bgcolor: theme.palette.primary.main,
                                  '&:hover': { bgcolor: theme.palette.primary.dark }
                                }}
                              >
                                Manual Repay
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {(!loanSummary.installment_dates || loanSummary.installment_dates.length === 0) && (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No installments scheduled.</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Layout >
  );
};

export default AdminSummaryPage;
